(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-rf-related-world-cards]';
  const controllers = new WeakMap();

  function createController(root) {
    const track = root.querySelector('[data-rf-related-world-cards-track]');
    if (!track) return { destroy() {} };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoRotateSetting = root.dataset.rfAutoRotate !== 'false';
    const seconds = Math.max(2, Number(root.dataset.rfAutoRotateSeconds || 5));
    const listeners = [];

    let timer = 0;
    let resumeTimer = 0;
    let pageScrollTimer = 0;
    let resizeTimer = 0;
    let scrollRaf = 0;
    let activeColumn = 0;
    let isVisible = !('IntersectionObserver' in window);
    let isInteracting = false;
    let observer = null;
    let resizeObserver = null;

    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    }

    function cards() {
      return Array.from(track.querySelectorAll('[data-rf-related-world-card]'));
    }

    function columnPositions() {
      const positions = [];
      const paddingLeft = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;

      cards().forEach((card) => {
        const x = Math.max(0, Math.round(card.offsetLeft - paddingLeft));
        if (!positions.some((position) => Math.abs(position - x) <= 2)) positions.push(x);
      });

      return positions.sort((a, b) => a - b);
    }

    function nearestColumn() {
      const positions = columnPositions();
      if (!positions.length) return 0;

      const current = track.scrollLeft;
      let best = 0;
      let distance = Infinity;

      positions.forEach((x, index) => {
        const nextDistance = Math.abs(x - current);
        if (nextDistance < distance) {
          distance = nextDistance;
          best = index;
        }
      });

      return best;
    }

    function goToColumn(index, smooth = true) {
      const positions = columnPositions();
      if (positions.length <= 1) return;

      const safeIndex = ((index % positions.length) + positions.length) % positions.length;
      activeColumn = safeIndex;
      track.scrollTo({
        left: positions[safeIndex],
        behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto'
      });
    }

    function stopTimer() {
      if (timer) window.clearTimeout(timer);
      timer = 0;
    }

    function canRotate() {
      return (
        autoRotateSetting &&
        !reduceMotion.matches &&
        !document.hidden &&
        isVisible &&
        !isInteracting &&
        columnPositions().length > 1
      );
    }

    function schedule(delay = seconds * 1000) {
      stopTimer();
      if (!canRotate()) return;

      timer = window.setTimeout(() => {
        timer = 0;
        if (!canRotate()) return;
        activeColumn = nearestColumn();
        goToColumn(activeColumn + 1, true);
        schedule();
      }, Math.max(700, delay));
    }

    function pauseThenResume(delay = 2400) {
      stopTimer();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        isInteracting = false;
        schedule();
      }, delay);
    }

    function beginInteraction() {
      isInteracting = true;
      stopTimer();
      if (resumeTimer) window.clearTimeout(resumeTimer);
    }

    function handleTrackScroll() {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        activeColumn = nearestColumn();
      });
    }

    function handlePageScroll() {
      if (!isVisible || !autoRotateSetting) return;
      stopTimer();
      if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
      pageScrollTimer = window.setTimeout(() => schedule(), 450);
    }

    function realign() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        activeColumn = nearestColumn();
        goToColumn(activeColumn, false);
      }, 120);
    }

    listen(track, 'scroll', handleTrackScroll, { passive: true });
    listen(track, 'touchstart', beginInteraction, { passive: true });
    listen(track, 'touchend', () => pauseThenResume(), { passive: true });
    listen(track, 'touchcancel', () => pauseThenResume(), { passive: true });
    listen(track, 'pointerdown', beginInteraction, { passive: true });
    listen(track, 'pointerup', () => pauseThenResume(), { passive: true });
    listen(track, 'pointercancel', () => pauseThenResume(), { passive: true });
    listen(track, 'mouseenter', beginInteraction);
    listen(track, 'mouseleave', () => pauseThenResume(1000));
    listen(track, 'focusin', beginInteraction);
    listen(track, 'focusout', () => pauseThenResume(1000));
    listen(track, 'wheel', () => {
      beginInteraction();
      pauseThenResume();
    }, { passive: true });
    listen(window, 'scroll', handlePageScroll, { passive: true });
    listen(window, 'resize', realign, { passive: true });
    listen(document, 'visibilitychange', () => {
      if (document.hidden) stopTimer();
      else schedule();
    });

    if (typeof reduceMotion.addEventListener === 'function') {
      listen(reduceMotion, 'change', () => {
        if (reduceMotion.matches) stopTimer();
        else schedule();
      });
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        isVisible = Boolean(entry && entry.isIntersecting);
        if (isVisible) schedule();
        else stopTimer();
      }, { threshold: 0.12, rootMargin: '12% 0px 12% 0px' });
      observer.observe(root);
    }

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(realign);
      resizeObserver.observe(track);
    }

    schedule();

    return {
      destroy() {
        stopTimer();
        if (resumeTimer) window.clearTimeout(resumeTimer);
        if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
        if (resizeTimer) window.clearTimeout(resizeTimer);
        if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
        if (observer) observer.disconnect();
        if (resizeObserver) resizeObserver.disconnect();
        listeners.splice(0).forEach((remove) => remove());
      }
    };
  }

  function init(root) {
    if (!root || controllers.has(root)) return;
    controllers.set(root, createController(root));
  }

  function destroy(root) {
    if (!root) return;
    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
  }

  function scan(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  const boot = () => scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches && event.target.matches(ROOT_SELECTOR)) destroy(event.target);
    if (event.target.querySelectorAll) event.target.querySelectorAll(ROOT_SELECTOR).forEach(destroy);
  });
})();
